/**
 * Runs before React mounts. Supabase invite links often land on Site URL (EHR root) with
 * `#access_token=...&type=invite`. Patients must complete password on the patient app with
 * the same hash so the client can establish a session there.
 */
function decodeJwtPayload(accessToken: string): Record<string, unknown> | null {
  try {
    const parts = accessToken.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function inviteRoleFromAccessToken(token: string): string | undefined {
  const payload = decodeJwtPayload(token);
  const meta = payload?.user_metadata as { role?: string } | undefined;
  if (meta?.role) return meta.role;
  const appMeta = payload?.app_metadata as { role?: string } | undefined;
  return appMeta?.role;
}

/**
 * If URL has invite tokens on the EHR host, rewrite to the correct set-password page (same hash).
 * @returns true if `location.replace` was called — the invite hash must NOT be processed by
 *   Supabase on this origin first, or the session is consumed and the patient app gets
 *   `session_not_found` / "Auth session missing!".
 */
export function bootstrapEhrInviteHash(): boolean {
  if (typeof window === "undefined") return false;

  const hash = window.location.hash;
  if (!hash || !hash.includes("access_token")) {
    return false;
  }

  const isCallback =
    hash.includes("type=invite") ||
    hash.includes("type=recovery") ||
    hash.includes("type=signup");
  if (!isCallback) {
    return false;
  }

  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const t = params.get("type");
  if (t !== "invite" && t !== "recovery" && t !== "signup") return false;

  const accessToken = params.get("access_token");
  if (!accessToken) return false;

  const metaRole = inviteRoleFromAccessToken(accessToken);

  const patientOrigin = (
    process.env.REACT_APP_PATIENTS_PORTAL_URL || "https://patient.getbiocure.com"
  ).replace(/\/$/, "");

  if (metaRole === "patient") {
    window.location.replace(`${patientOrigin}/set-new-password${hash}`);
    return true;
  }

  if (window.location.pathname === "/set-new-password") return false;

  window.location.replace(
    `${window.location.origin}/set-new-password${hash}`,
  );
  return true;
}
